import { useQuery } from "@tanstack/react-query";
import {
  api,
  DashboardValueStats,
  AdvisorDashboardSummaryResponse,
  AdvisorSummaryCard,
  AdvisorDashboardValueStats,
  AdvisorProgressItem,
  AdvisorPabOverview,
  PABCard,
  GoalRow,
  PABSessionDetailed,
  AdvisorMeeting,
  AdvisorsAvailabilityResponse,
  IndividualAdvisorAvailabilityResponse,
} from "@/services/api";

const ADVISOR_DASHBOARD_SUMMARY_QUERY_KEY = ["advisor-dashboard-summary"] as const;

const mapAdvisorSummaryCards = (cards: AdvisorSummaryCard[]): Record<string, number> => {
  const summaryCards: Record<string, number> = {};
  cards.forEach((card) => {
    summaryCards[card.title] = card.value;
  });
  return summaryCards;
};

export const useDashboardValueStats = () => {
  return useQuery<DashboardValueStats>({
    queryKey: ["dashboard-value-stats"],
    queryFn: api.dashboard.getValueStats,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 15 * 60 * 1000, // Refetch every 15 minutes
  });
};

export const useAdvisorDashboardValueStats = () => {
  return useQuery<
    AdvisorDashboardSummaryResponse,
    Error,
    AdvisorDashboardValueStats,
    typeof ADVISOR_DASHBOARD_SUMMARY_QUERY_KEY
  >({
    queryKey: ADVISOR_DASHBOARD_SUMMARY_QUERY_KEY,
    queryFn: api.dashboard.getAdvisorDashboardSummary,
    select: (response) => ({
      summaryCards: mapAdvisorSummaryCards(response.summaryCards),
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useAdvisorPABs = (options?: { enabled?: boolean }) => {
  return useQuery<
    AdvisorDashboardSummaryResponse,
    Error,
    PABCard[],
    typeof ADVISOR_DASHBOARD_SUMMARY_QUERY_KEY
  >({
    queryKey: ADVISOR_DASHBOARD_SUMMARY_QUERY_KEY,
    queryFn: api.dashboard.getAdvisorDashboardSummary,
    select: (response) => response.pabCards,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useAdvisorGoals = () => {
  return useQuery<GoalRow[]>({
    queryKey: ["advisor-goals"],
    queryFn: api.dashboard.getAdvisorGoals,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useAdvisorPabInvites = (options?: { enabled?: boolean }) => {
  return useQuery<PABCard[]>({
    queryKey: ["advisor-pab-invites"],
    queryFn: api.pabSessions.getAdvisorPabInvites,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useAdvisorProgressItems = (options?: { enabled?: boolean }) => {
  return useQuery<AdvisorProgressItem[]>({
    queryKey: ["advisor-progress-items"],
    queryFn: () => api.dashboard.getAdvisorProgressItems(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5 * 60 * 1000,
  });
};

export const useAdvisorPabOverviews = (options?: { enabled?: boolean }) => {
  return useQuery<AdvisorPabOverview[]>({
    queryKey: ["advisor-pab-overviews"],
    queryFn: () => api.dashboard.getAdvisorPabOverviews(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5 * 60 * 1000,
  });
};

export const usePABSessionDetailed = (sessionId: number | null) => {
  return useQuery<PABSessionDetailed>({
    queryKey: ["pab-session-detailed", sessionId],
    queryFn: () => api.pabSessions.getDetailed(sessionId!),
    enabled: !!sessionId, // Only run query when sessionId is provided
    staleTime: 10 * 60 * 1000, // 10 minutes - goals don't change frequently
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useAdvisorMeetings = () => {
  return useQuery<AdvisorMeeting[]>({
    queryKey: ["advisor-meetings"],
    queryFn: api.advisors.getMeetings,
    staleTime: 2 * 60 * 1000, // 2 minutes - meetings can change frequently
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes for real-time updates
  });
};

export const useAdvisorPastMeetings = () => {
  return useQuery<AdvisorMeeting[]>({
    queryKey: ["advisor-past-meetings"],
    queryFn: api.advisors.getPastMeetings,
    staleTime: 5 * 60 * 1000, // 5 minutes - past meetings don't change as frequently
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useAdvisorsAvailability = (sessionId: number | null) => {
  return useQuery<AdvisorsAvailabilityResponse>({
    queryKey: ["advisors-availability", sessionId],
    queryFn: () => api.pabSessions.getAdvisorsAvailability(sessionId!),
    enabled: !!sessionId, // Only run query when sessionId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes - availability can change frequently
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useIndividualAdvisorAvailability = (
  sessionId: number | null,
  advisorId: number | null,
) => {
  return useQuery<IndividualAdvisorAvailabilityResponse>({
    queryKey: ["individual-advisor-availability", sessionId, advisorId],
    queryFn: () => api.pabSessions.getIndividualAdvisorAvailability(sessionId!, advisorId!),
    enabled: !!(sessionId && advisorId), // Only run query when both IDs are provided
    staleTime: 5 * 60 * 1000, // 5 minutes - availability can change frequently
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export default useDashboardValueStats;
